/**
 * META-1 (PR #352): useAgentAssistant hook.
 *
 * Gestiona conversación + tool calls + confirmation flow + memorias del
 * asistente del agente. Encapsula el chat 360 para AgentChatSidebar.
 */
import { useCallback, useState } from 'react';
import {
  agentAssistantApi,
  AgentChatResult,
  AgentMemory,
  PendingConfirmation,
  ToolCallResult,
} from '../api/agent-assistant-api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCallResult[];
  timestamp: string;
}

export interface UseAgentAssistantResult {
  messages: ChatMessage[];
  sending: boolean;
  pendingConfirmation: PendingConfirmation | null;
  memories: AgentMemory[];
  send: (text: string) => Promise<void>;
  confirmAction: () => Promise<void>;
  cancelConfirmation: () => void;
  refreshMemories: () => Promise<void>;
  clearConversation: () => void;
}

export function useAgentAssistant(): UseAgentAssistantResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingConfirmation | null>(null);
  const [memories, setMemories] = useState<AgentMemory[]>([]);

  const appendAssistant = useCallback((result: AgentChatResult) => {
    const assistantMsg: ChatMessage = {
      role: 'assistant',
      content: result.message,
      toolCalls: result.toolCalls,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, assistantMsg]);
    if (result.pendingConfirmation) {
      setPendingConfirmation(result.pendingConfirmation);
    }
  }, []);

  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || sending) return;
      const userMsg: ChatMessage = {
        role: 'user',
        content: text,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setSending(true);
      setPendingConfirmation(null);
      try {
        const result = await agentAssistantApi.chat({ message: text });
        appendAssistant(result);
      } catch (err: any) {
        const errorMsg: ChatMessage = {
          role: 'assistant',
          content:
            err?.response?.data?.message ?? err?.message ?? 'Error en el chat. Intenta de nuevo.',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setSending(false);
      }
    },
    [sending, appendAssistant],
  );

  const confirmAction = useCallback(async () => {
    if (!pendingConfirmation) return;
    setSending(true);
    try {
      const result = await agentAssistantApi.chat({
        message: `Confirmar ${pendingConfirmation.toolName}`,
        confirmedToolCallId: pendingConfirmation.toolName,
        confirmedToolInput: pendingConfirmation.input,
      });
      appendAssistant(result);
      setPendingConfirmation(null);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content:
          err?.response?.data?.message ?? err?.message ?? 'No se pudo ejecutar la acción.',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setSending(false);
    }
  }, [pendingConfirmation, appendAssistant]);

  const cancelConfirmation = useCallback(() => {
    setPendingConfirmation(null);
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: 'Acción cancelada por el usuario.',
        timestamp: new Date().toISOString(),
      },
    ]);
  }, []);

  const refreshMemories = useCallback(async () => {
    try {
      const data = await agentAssistantApi.listMemories();
      setMemories(data);
    } catch {
      // Silent — memorias no son críticas
    }
  }, []);

  const clearConversation = useCallback(() => {
    setMessages([]);
    setPendingConfirmation(null);
  }, []);

  return {
    messages,
    sending,
    pendingConfirmation,
    memories,
    send,
    confirmAction,
    cancelConfirmation,
    refreshMemories,
    clearConversation,
  };
}
